import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import { toRgba } from '../../../../ui/theme/colors';
import Icon from '../../../../ui/Icon';

const Container = styled(Flex)`
  background: linear-gradient(135deg, ${theme.colors.PRIMARY} 0%, ${theme.colors.PRIMARY} 100%);
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 16px ${toRgba(theme.colors.PRIMARY, 0.2)};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 150px;
    height: 150px;
    background: ${theme.colors.NEU_0};
    opacity: 0.1;
    border-radius: 50%;
  }
`;

const Header = styled(Flex)`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.NEU_0}40;
  position: relative;
  z-index: 1;
`;

const SubSection = styled(Flex)`
  margin-bottom: 20px;
  position: relative;
  z-index: 1;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SuccessStoryCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background: ${theme.colors.NEU_0};
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid ${theme.colors.NEU_0}20;
  box-shadow: 0 2px 6px ${toRgba(theme.colors.NEU_999, 0.1)};

  &:hover {
    background: ${theme.colors.NEU_0};
    box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.15)};
    transform: translateY(-2px);
    border-color: ${theme.colors.NEU_0}40;
  }
`;

const StoryTitle = styled(Typo.MediumText)`
  color: ${theme.colors.PRIMARY};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.2s ease;
`;

const TrendItem = styled(Flex)`
  padding: 10px 12px;
  border-radius: 6px;
  background: ${theme.colors.NEU_0}30;
  border: 1px solid ${theme.colors.NEU_0}40;
  backdrop-filter: blur(10px);
`;

const InspirationSection = ({ inspiration, goalTitle, compact = false }) => {
  const { benchmarks, successStories, marketTrends } = inspiration;

  return (
    <Container direction='column'>
      <Header direction='column' gap='8'>
        <Flex align='center' gap='8'>
          <Icon.Lightbulb size='medium' color={theme.colors.NEU_0} />
          <Typo.ContentSmallHeader color={theme.colors.NEU_0}>Tips & Resources</Typo.ContentSmallHeader>
        </Flex>
        <Typo.SmallText color={theme.colors.NEU_0}>Learn from success stories and industry insights</Typo.SmallText>
      </Header>

      {successStories && successStories.length > 0 && (
        <SubSection direction='column' gap='12'>
          <Flex direction='column' gap='12'>
            {successStories.slice(0, 3).map((story, index) => (
              <SuccessStoryCard key={index} href={story.url} target='_blank' rel='noopener noreferrer'>
                <StoryTitle>
                  {story.title}
                  <Icon.ExternalLink size='x-small' color={theme.colors.PRIMARY} />
                </StoryTitle>
                <Typo.SmallText>{story.excerpt}</Typo.SmallText>
              </SuccessStoryCard>
            ))}
          </Flex>
        </SubSection>
      )}

      {marketTrends && marketTrends.length > 0 && (
        <SubSection direction='column' gap='12'>
          <Typo.SmallText fontWeight='600' color={theme.colors.NEU_0}>
            Market Insights
          </Typo.SmallText>
          <Flex direction='column' gap='8'>
            {marketTrends.map((trend, index) => (
              <TrendItem key={index} align='center' gap='10' width='100%'>
                <Icon.TrendingUp size='x-small' color={theme.colors.NEU_0} />
                <Typo.SmallText color={theme.colors.NEU_0}>{trend}</Typo.SmallText>
              </TrendItem>
            ))}
          </Flex>
        </SubSection>
      )}
    </Container>
  );
};

export default InspirationSection;
